import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail, setFallbackToken, setActiveOrg } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const adopt = useCallback((data) => {
    setUser(data);
    setActiveOrg(data?.tenant_id || null);
  }, []);

  const refresh = useCallback(async () => {
    const { data } = await api.get("/auth/me");
    adopt(data);
    return data;
  }, [adopt]);

  useEffect(() => {
    let active = true;
    api
      .get("/auth/me")
      .then(({ data }) => {
        if (active) adopt(data);
      })
      .catch((e) => {
        if (!active) return;
        if (e.response?.status !== 401) console.error("Session check failed", e.message);
        setFallbackToken(null);
        setActiveOrg(null);
        setUser(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [adopt]);

  const authenticate = useCallback(
    async (path, payload) => {
      try {
        setFallbackToken(null);
        setActiveOrg(null);
        const { data } = await api.post(path, payload);
        // Confirm the httpOnly cookie survived (blocked on some mobile browsers).
        try {
          await api.get("/auth/me");
        } catch (probeError) {
          if (probeError.response?.status === 401) setFallbackToken(data.token);
        }
        adopt(data.user);
        return { ok: true, user: data.user };
      } catch (e) {
        return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
      }
    },
    [adopt]
  );

  const login = useCallback((email, password) => authenticate("/auth/login", { email, password }), [authenticate]);
  const signup = useCallback((payload) => authenticate("/auth/signup", payload), [authenticate]);
  const acceptInvite = useCallback((payload) => authenticate("/invitations/accept", payload), [authenticate]);

  const switchOrg = useCallback(
    async (orgId) => {
      try {
        setActiveOrg(orgId);
        const { data } = await api.post("/auth/switch-org", { org_id: orgId });
        adopt(data);
        return { ok: true };
      } catch (e) {
        setActiveOrg(user?.tenant_id || null);
        return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
      }
    },
    [adopt, user]
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* clearing local state is what matters */
    }
    setFallbackToken(null);
    setActiveOrg(null);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      login,
      signup,
      logout,
      refresh,
      switchOrg,
      acceptInvite,
      org: user?.active_organization || null,
      orgs: user?.organizations || [],
    }),
    [user, loading, login, signup, logout, refresh, switchOrg, acceptInvite]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
